import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useMatch, useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {useOnMount} from '@edna/hooks';
import {ECurrencySymbol} from '@edna/utils';

import {FieldsRow, Text} from 'src/components/primitives';
import {EChannelType} from 'src/constants';
import {useGetLeadClientQuery} from 'src/containers/LeadCompanyClients/api';
import {useGetSupportClientQuery} from 'src/containers/SupportClients/api';
import {TTariffDetailFullInfo} from 'src/containers/TariffsCatalog';
import PATHS from 'src/paths';

import {TCreateAvailableTariffFormData, TLicenseInfo} from '../definitions';
import {useLicenseInfo} from '../useLicenseInfo';
import {COLUMNS_WIDTH, getFinalPrice} from '../utils';
import * as S from './style';

const CORRECTION_MULTIPLIER = 100_000_000;
const COEFFICIENT_DECIMAL_SCALE = 8;
const PRICE_DECIMAL_SCALE = 2;
const BASE_COEFFICIENT = 1;

const getCoefficient = (initialPrice: number, finalPrice?: number | string | null) => {
  if (!finalPrice) {
    return BASE_COEFFICIENT;
  }

  const price = Number(finalPrice);
  const coefficient =
    Math.ceil((price / initialPrice) * CORRECTION_MULTIPLIER) / CORRECTION_MULTIPLIER;

  return coefficient;
};

type TFieldName = keyof TCreateAvailableTariffFormData;

const mapFinalPriceToLicenseInfoKey = {
  license: 'licensePrice',
  licensePrice: 'licensePrice',
  connectionFee: 'connectionFee',
  connectionFeePrice: 'connectionFee',
  amau: 'amauPrice',
  amauPrice: 'amauPrice',
} as Record<TFieldName, keyof TLicenseInfo>;

type TProps = {
  channelType: EChannelType;
  license?: TTariffDetailFullInfo;
  readOnly?: boolean;
};

export const CoefficientsForm = React.memo<TProps>(({readOnly, license, channelType}) => {
  const {t} = useTranslation();
  const {id} = useParams();
  const isLeadClientView = !!useMatch(`${PATHS.COMPANY_CLIENTS}/*`);

  const {getValues, setValue, reset} = useFormContext<TCreateAvailableTariffFormData>();

  const {data: supportClient} = useGetSupportClientQuery(id ?? skipToken, {
    skip: !!license || isLeadClientView,
  });
  const {data: leadClient} = useGetLeadClientQuery(id ?? skipToken, {skip: !isLeadClientView});

  const licenseInfo = useLicenseInfo({license});

  const setPrices = React.useCallback(() => {
    const formValues = getValues();

    reset({
      ...getValues(),
      licensePrice: getFinalPrice(licenseInfo.licensePrice, formValues.license) as number,
      connectionFeePrice: getFinalPrice(
        licenseInfo.connectionFee,
        formValues.connectionFee,
      ) as number,
      amauPrice: getFinalPrice(licenseInfo.amauPrice, formValues.amau) as number,
    });
  }, [licenseInfo]);

  useOnMount(() => setPrices());
  React.useLayoutEffect(() => {
    setPrices();
  }, [licenseInfo.licensePrice, licenseInfo.amauPrice, licenseInfo.connectionFee]);

  const handleChangePrice = React.useCallback(
    (priceFieldName: TFieldName, coefficientFieldName: TFieldName) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldValue = event.target.value.replaceAll(' ', '');
        const licenseInfoKey = mapFinalPriceToLicenseInfoKey[priceFieldName];
        const licensePrice = licenseInfo[licenseInfoKey] as number;

        if (!fieldValue) {
          setValue(priceFieldName, licensePrice);
          setValue(coefficientFieldName, BASE_COEFFICIENT);

          return;
        }

        setValue(priceFieldName, getFinalPrice(licensePrice, Number(fieldValue)));
      },
    [licenseInfo],
  );

  const handleChangeCoefficient = React.useCallback(
    (coefficientFieldName: TFieldName, priceFieldName: TFieldName) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldValue = event.target.value.replaceAll(' ', '');
        const licenseInfoKey = mapFinalPriceToLicenseInfoKey[coefficientFieldName];
        const licensePrice = licenseInfo[licenseInfoKey] as number;

        if (!fieldValue) {
          setValue(coefficientFieldName, BASE_COEFFICIENT);
          setValue(priceFieldName, licensePrice);

          return;
        }

        setValue(coefficientFieldName, getCoefficient(licensePrice, fieldValue));
      },
    [licenseInfo],
  );

  const currency = React.useMemo(() => {
    const currencyValue =
      license?.currency ?? supportClient?.defaultCurrency ?? leadClient?.currencyCode;

    return currencyValue ? ECurrencySymbol[currencyValue] : null;
  }, [supportClient, leadClient, license]);

  return (
    <S.Section>
      <FieldsRow size="5" columnsWidth={COLUMNS_WIDTH}>
        <S.Label htmlFor="baseLicensePrice">
          {t('Tariffs:editTariffModal.baseLicensePrice', {currency})}
        </S.Label>
        <S.NumberInput value={licenseInfo.licensePrice} readOnly />
        <S.Label htmlFor="license">{t('Tariffs:editTariffModal.coefficient')}</S.Label>
        <S.NumberInputField
          name="license"
          readOnly={readOnly}
          onBlur={handleChangePrice('licensePrice', 'license')}
          decimalScale={COEFFICIENT_DECIMAL_SCALE}
        />
        <S.Label htmlFor="licensePrice">
          {t('Tariffs:editTariffModal.licensePrice', {currency})}
        </S.Label>
        <S.NumberInputField
          name="licensePrice"
          readOnly={readOnly}
          onBlur={handleChangeCoefficient('license', 'licensePrice')}
          decimalScale={PRICE_DECIMAL_SCALE}
        />
      </FieldsRow>
      {channelType === EChannelType.WHATSAPP && (
        <>
          <FieldsRow size="5" columnsWidth={COLUMNS_WIDTH}>
            <S.Label htmlFor="baseConnectionFee">
              {t('Tariffs:editTariffModal.baseConnectionFee', {currency})}
            </S.Label>
            <S.NumberInput value={licenseInfo.connectionFee} readOnly />
            <S.Label htmlFor="connectionFee">{t('Tariffs:editTariffModal.coefficient')}</S.Label>
            <S.NumberInputField
              name="connectionFee"
              readOnly={readOnly}
              onBlur={handleChangePrice('connectionFeePrice', 'connectionFee')}
              decimalScale={COEFFICIENT_DECIMAL_SCALE}
            />
            <S.Label htmlFor="connectionFee">
              {t('Tariffs:editTariffModal.connectionFee', {currency})}
            </S.Label>
            <S.NumberInputField
              readOnly={readOnly}
              name="connectionFeePrice"
              onBlur={handleChangeCoefficient('connectionFee', 'connectionFeePrice')}
              decimalScale={PRICE_DECIMAL_SCALE}
            />
          </FieldsRow>
          <FieldsRow size="5" columnsWidth={COLUMNS_WIDTH}>
            <S.Label htmlFor="baseAmau">
              {t('Tariffs:editTariffModal.baseAmau', {currency})}
            </S.Label>
            <S.NumberInput value={licenseInfo.amauPrice} readOnly />
            <S.Label htmlFor="amau">{t('Tariffs:editTariffModal.coefficient')}</S.Label>
            <S.NumberInputField
              name="amau"
              readOnly={readOnly}
              onBlur={handleChangePrice('amauPrice', 'amau')}
              decimalScale={COEFFICIENT_DECIMAL_SCALE}
            />
            <S.Label htmlFor="amau">{t('Tariffs:editTariffModal.amau', {currency})}</S.Label>
            <S.NumberInputField
              name="amauPrice"
              readOnly={readOnly}
              onBlur={handleChangeCoefficient('amau', 'amauPrice')}
              decimalScale={PRICE_DECIMAL_SCALE}
            />
          </FieldsRow>
        </>
      )}
      <Text secondary size="XS">
        {t('Tariffs:pricesIsWithoutVat')}
      </Text>
    </S.Section>
  );
});

CoefficientsForm.displayName = 'CoefficientsForm';
